"use client";

import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from "react";

import OfferImpression, { type OfferImpressionFields } from "@/components/OfferImpression";
import { resolvePlacementContract } from "@/lib/offer-placement-contract";
import type {
  PlacementContract,
  RuntimeOfferPlacementActionMode,
  RuntimeOfferPlacementIntent,
  RuntimeOfferPlacementKeyForOwner,
  RuntimeOfferPlacementOwner,
  RuntimeOfferPlacementPlan,
} from "@/lib/offer-placement-runtime.generated";

export type GovernedOfferPlacementKey = RuntimeOfferPlacementKeyForOwner<"GovernedOfferPlacement">;

export interface GovernedOfferPlacementContext<K extends GovernedOfferPlacementKey> {
  contract: PlacementContract<K>;
  markActionReady: () => void;
  analyticsContext: {
    placement: K;
    sourcePath: string;
    intent: RuntimeOfferPlacementIntent<K>;
    presentationId: string;
  };
}

export interface GovernedOfferPlacementProps<
  K extends GovernedOfferPlacementKey,
> extends OfferImpressionFields {
  placement: K;
  planId: RuntimeOfferPlacementPlan<K>;
  actionMode: RuntimeOfferPlacementActionMode<K>;
  sourcePath: string;
  intent: RuntimeOfferPlacementIntent<K>;
  presentationScope?: string;
  ready?: boolean;
  className?: string;
  style?: CSSProperties;
  children: (context: GovernedOfferPlacementContext<K>) => ReactNode;
}

/**
 * Registry-backed offer boundary: validation, presentation identity, visible
 * exact-once impression, and checkout attribution travel as one contract.
 */
export default function GovernedOfferPlacement<K extends GovernedOfferPlacementKey>({
  placement,
  planId,
  actionMode,
  sourcePath,
  intent,
  presentationScope,
  ready,
  className,
  style,
  children,
  ...impressionFields
}: GovernedOfferPlacementProps<K>) {
  const targetRef = useRef<HTMLDivElement>(null);
  const [readyPresentationId, setReadyPresentationId] = useState<string | null>(null);
  const contract = resolvePlacementContract({
    placement,
    runtimeOwner: "GovernedOfferPlacement" as RuntimeOfferPlacementOwner<K>,
    planId,
    actionMode,
    sourcePath,
    intent,
    presentationScope,
  });
  const markActionReady = useCallback(
    () => setReadyPresentationId(contract.presentationId),
    [contract.presentationId]
  );
  const impressionReady = ready ?? readyPresentationId === contract.presentationId;
  const analyticsContext = {
    placement: contract.placement,
    sourcePath: contract.sourcePath,
    intent: contract.intent,
    presentationId: contract.presentationId,
  };

  return (
    <div
      ref={targetRef}
      className={className}
      style={style}
      data-offer-placement={contract.placement}
      data-offer-presentation={contract.presentationId}
    >
      <OfferImpression
        {...impressionFields}
        planId={contract.planId}
        actionMode={contract.actionMode}
        sourcePath={contract.sourcePath}
        intent={contract.intent}
        placement={contract.placement}
        presentationKey={contract.presentationId}
        ready={impressionReady}
        targetRef={targetRef}
      />
      {children({ contract, markActionReady, analyticsContext })}
    </div>
  );
}
